import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * DashboardCard
 *
 * Base card component for dashboard widgets. Provides consistent styling
 * with optional top accent bar for highlighting.
 *
 * Props:
 *  - className: Column wrapper classes (e.g., "col-lg-3 col-md-6 mb-3")
 *  - accent: Optional accent color for top bar ('purple' | 'green' | 'primary' | custom CSS gradient)
 *  - padding: Card body padding class (default: "p-4"). Use the same value across dashboard widgets for even edges.
 *  - fillRowHeight: When true (default), card uses h-100 so widgets in a row match the tallest column.
 *    Set false for natural height (e.g. assign + tasks row).
 *  - children: Card content
 */
export default function DashboardCard({
  className = '',
  accent = null,
  padding = 'p-4',
  fillRowHeight = true,
  children,
}) {
  const accentClassMap = {
    purple: 'dashboard-card-accent-purple',
    green: 'dashboard-card-accent-green',
    primary: 'dashboard-card-accent-primary',
  };

  const hasAccent = accent !== null;
  const accentClass = accentClassMap[accent];
  const isCustomAccent = hasAccent && !accentClass;

  const cardClasses = classNames('card shadow-sm dashboard-card', {
    'h-100': fillRowHeight,
    'dashboard-card-accented': hasAccent,
    [accentClass]: accentClass,
  });

  const cardStyle = isCustomAccent
    ? { '--dashboard-card-accent': accent }
    : undefined;

  return (
    <div className={className}>
      <div className={cardClasses} style={cardStyle}>
        <div className={classNames('card-body', padding)}>
          {children}
        </div>
      </div>
    </div>
  );
}

DashboardCard.propTypes = {
  className: PropTypes.string,
  accent: PropTypes.string,
  padding: PropTypes.string,
  fillRowHeight: PropTypes.bool,
  children: PropTypes.node.isRequired,
};
